import { Section } from '../../../Finger/Section';
import FingerWithAction, { Status } from '../../../atoms/finger-with-action';
import { SummaryRow } from '../../../Summary/SummaryRow';
import { formatCurrency, boolToYesNo } from '../../../../utilities/formatting';

import { Q } from '../../../../store/student/types';
import { UserRole } from '../../../../store/auth/types';
import { hasPerm, P } from '../../../../utilities/roles/permissions';
import { IRisk } from '../../../../types';
import { useSelector } from 'react-redux';
import { ApplicationState } from '../../../../store';
import CASBypassWarning from '../../../molecules/casBypassWarning/casBypassWarning';
import { useParams } from 'react-router-dom';
import { useCustomNavigate } from '../../../../hooks/useCustomNavigate';
import { Box } from '@chakra-ui/react';
import { DocumentRequestButtons } from '../../../DocumentRequestButtons';
import NoDocPlaceholder from '../../../atoms/no-doc-placeholder';

export interface IDispatchProps {
  onUpdateRisks: (source: string, clearedRisks: any) => void;
  onReset: () => void;
}

export interface IProps {
  hasStarted: boolean;
  complete: boolean;
  PCI1: Status;
  PCI2: Status;
  PCI3: Status;
  PCI4: Status;
  whyUk?: string;
  whyUkOther?: string;
  selectedCourseReasons?: string[];
  selectedCourseReasonsOther?: string;
  selectedUniReasons?: string[];
  selectedUniReasonsOther?: string;
  uniChoiceReason1?: string;
  uniChoiceReason2?: string;
  uniChoiceReason3?: string;
  campus?: string;
  considerOtherUni?: boolean;
  otherUni1?: string;
  otherUni2?: string;
  selectedResearchMethods?: string[];
  selectedResearchMethodsOther?: string;
  whyNotOtherUni?: string;
  considerOtherCourses?: boolean;
  considerOtherCourse?: string;
  whyNotConsiderOtherCourse?: string;
  courseAlignFutureCareer?: boolean;
  courseAlignFutureCareerOther?: string;
  workWhileStudy?: boolean;
  maxWorkHoursPerWeek?: string;
  whyWorkWhileStudy?: string;
  whyWorkWhileStudyOther?: string;
  affordCourseAndLivingWithoutWorking?: boolean;
  affordCourseAndLivingWithoutWorkingNo?: string;
  completeDegreeWorkCountry?: string;
  expectedSalary?: number;
  furtherStudyInUk?: boolean;
  furtherStudyCareerPlans?: string;
  liveWhileStudying?: string;
  liveWhileStudyingOther?: string;
  expectedWeeklyRent?: number;
  commutePlan?: string;
  expectedLivingExpenses?: number;
  awareOfUKVIInterview?: boolean;
  riskList: IRisk['preCasInterviewRisk'];
  clearedRisks?: IRisk['preCasInterviewRisk'];
  userRole: UserRole;
}

type Props = IProps & IDispatchProps;

function Interview(props: Props) {
  const { offerId = '' } = useParams();

  const { navigateTo } = useCustomNavigate();
  const onResume = (url: string) => navigateTo(`${offerId}/${url}`);

  const handleActionClick = (target: string) => {
    switch (target) {
      case 'PCI1':
        onResume('pre-cas-interview/step-1');
        break;
      case 'PCI2':
        onResume('pre-cas-interview/step-2');
        break;
      case 'PCI3':
        onResume('pre-cas-interview/step-3');
        break;
      case 'PCI4':
        onResume('pre-cas-interview/step-4');
        break;
    }
  };

  const handleUpdateRisksClick = (target: string, source: string) => {
    let clearedRisks = {};
    switch (target) {
      case 'PCI1':
        clearedRisks = {
          preCasInterviewRisk: {
            step1: props.riskList.step1 || [],
          },
        };
        break;
      case 'PCI2':
        clearedRisks = {
          preCasInterviewRisk: {
            step2: props.riskList.step2 || [],
          },
        };
        break;
      case 'PCI3':
        clearedRisks = {
          preCasInterviewRisk: {
            step3: props.riskList.step3 || [],
          },
        };
        break;
      case 'PCI4':
        clearedRisks = {
          preCasInterviewRisk: {
            step4: props.riskList.step4 || [],
          },
        };
        break;
    }
    props.onUpdateRisks(`Pre-CAS Interview - ${source}`, clearedRisks);
  };

  const checkCheckedOffRisks = (target: string): boolean => {
    if (props.clearedRisks) {
      switch (target) {
        case 'PCI1':
          return props.clearedRisks?.step1?.length > 0;
        case 'PCI2':
          return props.clearedRisks?.step2?.length > 0;
        case 'PCI3':
          return props.clearedRisks?.step3?.length > 0;
        case 'PCI4':
          return props.clearedRisks?.step4?.length > 0;
        default:
          return false;
      }
    }

    return false;
  };

  const canResume = hasPerm(props.userRole, P.PRE_CAS_INTERVIEW_EDIT);
  const canReset = hasPerm(props.userRole, P.PRE_CAS_INTERVIEW_RESET);
  const showRisks = hasPerm(props.userRole, P.PRE_CAS_INTERVIEW_FLAGS);
  const canCheckOffRisks = hasPerm(props.userRole, P.CHECK_OFF_RISKS);

  const isCasBypass = useSelector(({ student }: ApplicationState) => student.offer.casBypass);

  return (
    <Box>
      {isCasBypass && (
        <>
          <CASBypassWarning />

          <NoDocPlaceholder placeholder="No action required" />
        </>
      )}
      {!isCasBypass && (
        <>
          <Section label={'Pre-CAS Questionnaire'}>
            <FingerWithAction
              id={'PCI1'}
              key={'PCI1'}
              status={props.PCI1}
              noEdit={true}
              label={'Step 1'}
              canCheckOff={canCheckOffRisks}
              isCheckedOff={checkCheckedOffRisks('PCI1')}
              onCheckOffRisk={() => handleUpdateRisksClick('PCI1', 'Step 1')}
              onClick={() => handleActionClick('PCI1')}
              showAction={canResume}
            >
              <SummaryRow
                key="whyUk"
                label="Why did you choose to study in the UK?"
                value={props.whyUk}
                isRisk={showRisks && props.riskList?.step1?.includes(Q.WHY_UK)}
              />
              {(props.whyUk === 'Other' || props.whyUk === 'I am not sure why I chose to study in the UK') && (
                <SummaryRow
                  key="whyUkOther"
                  label="Other"
                  value={props.whyUkOther}
                  isRisk={showRisks && props.riskList?.step1?.includes(Q.WHY_UK_OTHER)}
                />
              )}
              <SummaryRow
                key="selectedCourseReasons"
                label="Why did you choose this course?"
                value={props.selectedCourseReasons && props.selectedCourseReasons.map(reason => reason).join(', ')}
                isRisk={showRisks && props.riskList?.step1?.includes(Q.SELECTED_COURSE_REASONS)}
              />
              {props.selectedCourseReasons && props.selectedCourseReasons.includes('Other') && (
                <SummaryRow
                  key="selectedCourseReasonsOther"
                  label="Other"
                  value={props.selectedCourseReasonsOther}
                  isRisk={showRisks && props.riskList?.step1?.includes(Q.SELECTED_COURSE_REASONS_OTHER)}
                />
              )}
              <SummaryRow
                key="selectedUniReasons"
                label="Why did you choose this university?"
                value={props.selectedUniReasons && props.selectedUniReasons.map(reason => reason).join(', ')}
                isRisk={showRisks && props.riskList?.step1?.includes(Q.SELECTED_UNI_REASONS)}
              />
              {props.selectedUniReasons && props.selectedUniReasons.includes('Other') && (
                <SummaryRow
                  key="selectedUniReasonsOther"
                  label="Other"
                  value={props.selectedUniReasonsOther}
                  isRisk={showRisks && props.riskList?.step1?.includes(Q.SELECTED_UNI_REASONS_OTHER)}
                />
              )}
              <SummaryRow
                key="uniChoiceReason1"
                title="In your own words, please provide three reasons you chose this university?"
                label="Reason 1"
                value={props.uniChoiceReason1}
                isRisk={showRisks && props.riskList?.step1?.includes(Q.UNI_CHOICE_REASON_1)}
              />
              <SummaryRow
                key="uniChoiceReason2"
                label="Reason 2"
                value={props.uniChoiceReason2}
                isRisk={showRisks && props.riskList?.step1?.includes(Q.UNI_CHOICE_REASON_2)}
              />
              <SummaryRow
                key="uniChoiceReason3"
                label="Reason 3"
                value={props.uniChoiceReason3}
                isRisk={showRisks && props.riskList?.step1?.includes(Q.UNI_CHOICE_REASON_3)}
              />
            </FingerWithAction>
            <FingerWithAction
              id={'PCI2'}
              key={'PCI2'}
              status={props.PCI2}
              noEdit={true}
              label={'Step 2'}
              canCheckOff={canCheckOffRisks}
              isCheckedOff={checkCheckedOffRisks('PCI2')}
              onCheckOffRisk={() => handleUpdateRisksClick('PCI2', 'Step 2')}
              onClick={() => handleActionClick('PCI2')}
              showAction={canResume}
            >
              <SummaryRow
                key="campus"
                label="Which campus are you studying at?"
                value={props.campus}
                isRisk={showRisks && props.riskList?.step2?.includes(Q.CAMPUS)}
              />
              {/* Hide this row if user did not enter the page */}
              {props.PCI2 !== Status.Todo && (
                <SummaryRow
                  key="considerOtherUni"
                  label="Did you consider any other university?"
                  value={boolToYesNo(props.considerOtherUni)}
                  isRisk={showRisks && props.riskList?.step2?.includes(Q.CONSIDER_OTHER_UNI)}
                />
              )}
              {props.considerOtherUni === true ? (
                <>
                  <SummaryRow
                    key="otherUni1"
                    title="What two universities did you consider?"
                    label="University 1"
                    value={props.otherUni1}
                    isRisk={showRisks && props.riskList?.step2?.includes(Q.OTHER_UNI_1)}
                  />
                  <SummaryRow
                    key="otherUni2"
                    label="University 2"
                    value={props.otherUni2}
                    isRisk={showRisks && props.riskList?.step2?.includes(Q.OTHER_UNI_2)}
                  />
                  <SummaryRow
                    key="selectedResearchMethods"
                    label="How did you research these universities?"
                    value={
                      props.selectedResearchMethods &&
                      props.selectedResearchMethods.map(research => research).join(', ')
                    }
                    isRisk={showRisks && props.riskList?.step2?.includes(Q.SELECTED_RESEARCH_METHODS)}
                  />
                  {props.selectedResearchMethods && props.selectedResearchMethods.includes('Other') && (
                    <SummaryRow
                      key="selectedResearchMethodsOther"
                      label="Other"
                      value={props.selectedResearchMethodsOther}
                      isRisk={showRisks && props.riskList?.step2?.includes(Q.SELECTED_RESEARCH_METHODS_OTHER)}
                    />
                  )}
                </>
              ) : (
                <SummaryRow
                  key="whyNotOtherUni"
                  label="Why did you not consider other universities?"
                  value={props.whyNotOtherUni}
                  isRisk={showRisks && props.riskList?.step2?.includes(Q.WHY_NOT_OTHER_UNI)}
                />
              )}
              <SummaryRow
                key="considerOtherCourses"
                label="Did you consider any other course?"
                value={boolToYesNo(props.considerOtherCourses)}
                isRisk={showRisks && props.riskList?.step2?.includes(Q.CONSIDER_OTHER_COURSES)}
              />
              {props.considerOtherCourses === true ? (
                <SummaryRow
                  key="considerOtherCourse"
                  label="What course did you consider?"
                  value={props.considerOtherCourse}
                  isRisk={showRisks && props.riskList?.step2?.includes(Q.CONSIDER_OTHER_COURSE)}
                />
              ) : (
                <SummaryRow
                  key="whyNotConsiderOtherCourse"
                  label="Why did you not consider other courses?"
                  value={props.whyNotConsiderOtherCourse}
                  isRisk={showRisks && props.riskList?.step2?.includes(Q.WHY_NOT_CONSIDER_OTHER_COURSE)}
                />
              )}
              <SummaryRow
                key="courseAlignFutureCareer"
                label="Does your course align with your future career plans?"
                value={boolToYesNo(props.courseAlignFutureCareer)}
                isRisk={showRisks && props.riskList?.step2?.includes(Q.COURSE_ALIGN_FUTURE_CAREER)}
              />
              {!props.courseAlignFutureCareer ? (
                <SummaryRow
                  key="courseAlignFutureCareerOther"
                  label="Please explain why"
                  value={props.courseAlignFutureCareerOther}
                  isRisk={showRisks && props.riskList?.step2?.includes(Q.COURSE_ALIGN_FUTURE_CAREER_OTHER)}
                />
              ) : null}
            </FingerWithAction>
            <FingerWithAction
              id={'PCI3'}
              key={'PCI3'}
              status={props.PCI3}
              noEdit={true}
              label={'Step 3'}
              canCheckOff={canCheckOffRisks}
              isCheckedOff={checkCheckedOffRisks('PCI3')}
              onCheckOffRisk={() => handleUpdateRisksClick('PCI3', 'Step 3')}
              onClick={() => handleActionClick('PCI3')}
              showAction={canResume}
            >
              <SummaryRow
                key="workWhileStudy"
                label="Do you plan to work while you study?"
                value={boolToYesNo(props.workWhileStudy)}
                isRisk={showRisks && props.riskList?.step3?.includes(Q.WORK_WHILE_STUDY)}
              />
              {props.workWhileStudy === true && (
                <>
                  <SummaryRow
                    key="maxWorkHoursPerWeek"
                    label="Do you know the maximum number of hours per week you are permitted to work on a student visa?"
                    value={props.maxWorkHoursPerWeek}
                    isRisk={showRisks && props.riskList?.step3?.includes(Q.MAX_WORK_HOURS_PER_WEEK)}
                  />
                  <SummaryRow
                    key="whyWorkWhileStudy"
                    label="Why are you planning to work part-time in the UK while you study?"
                    value={props.whyWorkWhileStudy}
                    isRisk={showRisks && props.riskList?.step3?.includes(Q.WHY_WORK_WHILE_STUDY)}
                  />
                  {props.whyWorkWhileStudy === 'Other' && (
                    <SummaryRow
                      key="whyWorkWhileStudyOther"
                      label="Other"
                      value={props.whyWorkWhileStudyOther}
                      isRisk={showRisks && props.riskList?.step3?.includes(Q.WHY_WORK_WHILE_STUDY_OTHER)}
                    />
                  )}
                  <SummaryRow
                    key="affordCourseAndLivingWithoutWorking"
                    label="Are you able to afford your course fees and living expenses without working in the UK?"
                    value={boolToYesNo(props.affordCourseAndLivingWithoutWorking)}
                    isRisk={showRisks && props.riskList?.step3?.includes(Q.AFFORD_COURSE_AND_LIVING_WITHOUT_WORKING)}
                  />
                  {props.affordCourseAndLivingWithoutWorking === false && (
                    <SummaryRow
                      key="affordCourseAndLivingWithoutWorkingNo"
                      label="Please explain"
                      value={props.affordCourseAndLivingWithoutWorkingNo}
                      isRisk={
                        showRisks && props.riskList?.step3?.includes(Q.AFFORD_COURSE_AND_LIVING_WITHOUT_WORKING_NO)
                      }
                    />
                  )}
                </>
              )}
              <SummaryRow
                key="completeDegreeWorkCountry"
                label="What country do you plan to work in when you complete your degree?"
                value={props.completeDegreeWorkCountry}
                isRisk={showRisks && props.riskList?.step3?.includes(Q.COMPLETE_DEGREE_WORK_COUNTRY)}
              />
              {!!props.expectedSalary && (
                <SummaryRow
                  key="expectedSalary"
                  label="Once you graduate, how much do you expect to earn per year in your home country?"
                  value={`£${formatCurrency(props.expectedSalary)}`}
                  isRisk={showRisks && props.riskList?.step3?.includes(Q.EXPECTED_SALARY)}
                />
              )}
              <SummaryRow
                key="furtherStudyInUk"
                label="Do you plan to continue on to further study in the UK after your course is complete?"
                value={boolToYesNo(props.furtherStudyInUk)}
                isRisk={showRisks && props.riskList?.step3?.includes(Q.FURTHER_STUDY_IN_UK)}
              />
              {props.furtherStudyInUk === true && (
                <SummaryRow
                  key="furtherStudyCareerPlans"
                  label="Explain how this fits into your career plans"
                  value={props.furtherStudyCareerPlans}
                  isRisk={showRisks && props.riskList?.step3?.includes(Q.FURTHER_STUDY_CAREER_PLANS)}
                />
              )}
            </FingerWithAction>
            <FingerWithAction
              id={'PCI4'}
              key={'PCI4'}
              status={props.PCI4}
              noEdit={true}
              label={'Step 4'}
              canCheckOff={canCheckOffRisks}
              isCheckedOff={checkCheckedOffRisks('PCI4')}
              onCheckOffRisk={() => handleUpdateRisksClick('PCI4', 'Step 4')}
              onClick={() => handleActionClick('PCI4')}
              showAction={canResume}
            >
              <SummaryRow
                key="liveWhileStudying"
                label="Where do you plan to live while studying?"
                value={props.liveWhileStudying}
                isRisk={showRisks && props.riskList?.step4?.includes(Q.LIVE_WHILE_STUDYING)}
              />
              {props.liveWhileStudying === 'Other' && (
                <SummaryRow
                  key="liveWhileStudyingOther"
                  label="Please provide more information"
                  value={props.liveWhileStudyingOther}
                  isRisk={showRisks && props.riskList?.step4?.includes(Q.LIVE_WHILE_STUDYING_OTHER)}
                />
              )}
              {!!props.expectedWeeklyRent && (
                <SummaryRow
                  key="expectedWeeklyRent"
                  label="How much do you expect to pay for accommodation per week?"
                  value={`£${formatCurrency(props.expectedWeeklyRent)}`}
                  isRisk={showRisks && props.riskList?.step4?.includes(Q.EXPECTED_WEEKLY_RENT)}
                />
              )}
              <SummaryRow
                key="commutePlan"
                label="How will you travel between your accommodation and the university campus?"
                value={props.commutePlan}
                isRisk={showRisks && props.riskList?.step4?.includes(Q.COMMUTE_PLAN)}
              />
              {!!props.expectedLivingExpenses && (
                <SummaryRow
                  key="expectedLivingExpenses"
                  label="How much do you expect to spend in living expenses per week? (e.g an amount to cover food, daily travel, school expenses, phone, socialising etc.)"
                  value={`£${formatCurrency(props.expectedLivingExpenses)}`}
                  isRisk={showRisks && props.riskList?.step4?.includes(Q.EXPECTED_LIVING_EXPENSES)}
                />
              )}
              <SummaryRow
                key="awareOfUKVIInterview"
                label="International students can be interviewed by UKVI prior to receiving a visa, were you aware of this?"
                value={boolToYesNo(props.awareOfUKVIInterview)}
                isRisk={showRisks && props.riskList?.step4?.includes(Q.AWARE_OF_UK_VI_INTERVIEW)}
              />
            </FingerWithAction>
          </Section>

          {canReset && <DocumentRequestButtons canReset onResetClick={props.onReset} />}
        </>
      )}
    </Box>
  );
}

export default Interview;
